/**
 * 菜单数据转换工具
 * 将数据库中的菜单数据转换为前端需要的格式
 */

import { MenuItem } from '../types/menu';

/**
 * 导航菜单项接口（官网导航菜单）
 */
export interface NavigationMenuItem {
  path: string;
  label: string;
  icon: string;
}

/**
 * 数据管理菜单项接口
 */
export interface DataManagementMenuItem {
  path: string;
  label: string;
  icon: string;
  description?: string;
}

/**
 * 从菜单数据中筛选并转换官网导航菜单
 * 筛选条件：path 以 /show/ 开头，且 menuType 为 'C'（菜单类型）
 */
export const getNavigationMenu = (menuData: MenuItem[]): NavigationMenuItem[] => {
  if (!menuData || menuData.length === 0) {
    return [];
  }

  // 筛选官网导航菜单（路径以 /show/ 开头）
  // status: 0 正常，1 停用（根据数据库定义）
  // visible: 0 显示，1 隐藏（根据数据库定义）
  // 实际数据中 status 和 visible 可能是 1 表示正常和显示
  // 需要同时检查两种可能的值格式
  
  // 调试：记录每个菜单项的过滤原因
  const filterDetails: any[] = [];
  
  const navigationMenus = menuData.filter(menu => {
    const reasons: string[] = [];
    
    // 基本条件检查：必须有 component（网站路径使用 component 字段）
    if (!menu.component) {
      reasons.push('缺少component');
      filterDetails.push({ id: menu.id, name: menu.menuName, reason: '缺少component', path: menu.path, component: menu.component });
      return false;
    }
    
    // component 路径必须以 /show/ 开头（网站导航路径）
    if (!menu.component.startsWith('/show/') && !menu.component.startsWith('show/')) {
      reasons.push(`component不符合要求: ${menu.component}`);
      filterDetails.push({ id: menu.id, name: menu.menuName, reason: 'component不符合要求', component: menu.component, path: menu.path });
      return false;
    }
    
    // menuType 检查（必须是 'C' 类型，但如果没有设置 menuType，也允许通过）
    // 如果 menuType 未定义或为空，允许通过（可能是旧数据）
    if (menu.menuType !== undefined && menu.menuType !== null && menu.menuType !== '' && menu.menuType !== 'C') {
      reasons.push(`menuType不是'C': ${menu.menuType}`);
      filterDetails.push({ id: menu.id, name: menu.menuName, reason: `menuType不是'C'`, menuType: menu.menuType });
      return false;
    }
    
    // status 检查：根据 useMenuDataSimple 中的查询条件 status: 1，表示查询的是正常状态的菜单
    // 但为了兼容不同的定义，我们先不过滤 status
    
    // visible 检查：1 表示显示，0 表示隐藏（根据数据库定义）
    // visible 是 number 类型，所以只需要比较数字
    // 如果 visible 为 0，表示隐藏，应该过滤掉
    if (menu.visible !== undefined && menu.visible !== null) {
      if (menu.visible === 0) {
        reasons.push('visible为0(隐藏)');
        filterDetails.push({ id: menu.id, name: menu.menuName, reason: 'visible为0(隐藏)', visible: menu.visible });
        return false; // 隐藏的菜单需要过滤掉
      }
    }
    
    // 如果通过所有检查
    if (reasons.length === 0) {
      filterDetails.push({ id: menu.id, name: menu.menuName, reason: '通过', component: menu.component, path: menu.path, menuType: menu.menuType, visible: menu.visible });
    }
    
    return true;
  });

  // 按 orderNum 排序
  const sortedMenus = navigationMenus.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));

  // 转换为导航菜单格式，使用 component 作为网站路径
  return sortedMenus.map(menu => {
    // 去掉菜单名后的"管理"两个字
    let label = menu.menuName;
    if (label && label.endsWith('管理')) {
      label = label.slice(0, -2);
    }
    
    return {
      path: menu.component.startsWith('/') ? menu.component : `/${menu.component}`, // 确保路径以 / 开头
      label: label,
      icon: menu.icon || 'fas fa-circle'
    };
  });
};

/**
 * 从菜单数据中筛选并转换数据管理菜单
 * 筛选条件：path 以 /data/ 开头，且 menuType 为 'C'（菜单类型）
 */
export const getDataManagementMenu = (menuData: MenuItem[]): DataManagementMenuItem[] => {
  // 筛选数据管理菜单（路径以 /data/ 开头）
  // status: 0 正常，1 停用（根据数据库定义）
  // visible: 0 显示，1 隐藏（根据数据库定义）
  // 实际数据中 status 和 visible 可能是 1 表示正常和显示
  const dataMenus = menuData.filter(menu => 
    menu.path && 
    menu.path.startsWith('/data/') && 
    menu.menuType === 'C'
  );

  // 按 orderNum 排序
  const sortedMenus = dataMenus.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));

  // 转换为数据管理菜单格式
  return sortedMenus.map(menu => ({
    path: menu.path,
    label: menu.menuName,
    icon: menu.icon || 'fas fa-circle',
    description: menu.remark || ''
  }));
};

